import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ATTRACTIONS_LOCATIONS } from '../data/attractionsLocations';
import { WDS_ATTRACTIONS_LOCATIONS } from '../data/wdsLocations';
import { TRANSPORT_LOCATIONS } from '../data/transportLocations';
import { getParkCoordinates } from '../config';
import {
  calculateDistanceInMeters,
  filterVisibleMapEntities,
  getMapEntityPrimaryLabel,
  normalizeMapEntity
} from '../utils/interactiveMapUtils';
import {
  buildMapTilesUrlTemplate,
  DEFAULT_MAP_TILES_VERSION,
  fetchMapTilesVersion,
  MAP_TILES_LAYER_OPTIONS
} from '../utils/mapTiles';
import useMapUserLocation from '../hooks/useMapUserLocation';
import MapUserLocationNotice from './MapUserLocationNotice';
import TransportPopup from './TransportPopup';
import 'leaflet/dist/leaflet.css';
import '../styles/components/interactive-map.css';

// Centres et zooms par défaut pour chaque parc
const PARK_CONFIG = {
  DLP: {
    center: [48.8722, 2.7758],
    zoom: 16,
    locations: ATTRACTIONS_LOCATIONS,
    isWDS: false,
    usesDisneyTiles: true,
    showTransport: true
  },
  WDS: {
    center: [48.8665, 2.7798],
    zoom: 17,
    locations: WDS_ATTRACTIONS_LOCATIONS,
    isWDS: true,
    usesDisneyTiles: true,
    showTransport: true
  }
};

// Fonction pour créer une icône de marqueur personnalisée
const createWaitTimeIcon = (waitTime, status, isWDS = false, entityType = 'ATTRACTION') => {
  let bgColor, textColor, displayText;
  let isShow = entityType === 'SHOW';
  let isRestaurant = entityType === 'RESTAURANT';
  
  if (isShow) {
    bgColor = '#8b5cf6'; // Violet pour les spectacles
    displayText = `<img src="/images/buttons/webp/SHOWS_WHITE.webp" alt="Show" style="width: 20px; height: 20px;" />`;
  } else if (isRestaurant) {
    bgColor = '#0f766e';
    displayText = `<img src="/images/buttons/webp/Restaurant_WHITE.webp" alt="Restaurant" style="width: 20px; height: 20px;" />`;
  } else if (status === 'CLOSED') {
    bgColor = '#6b7280';
    textColor = '#fff';
    displayText = '✕';
  } else if (status === 'DOWN') {
    bgColor = '#ef4444';
    textColor = '#fff';
    displayText = '⚠';
  } else if (status === 'REFURBISHMENT') {
    bgColor = '#f59e0b';
    textColor = '#fff';
    displayText = '🔧';
  } else if (waitTime === null || waitTime === undefined || waitTime < 0) {
    bgColor = '#9ca3af';
    textColor = '#fff';
    displayText = '?';
  } else if (waitTime === 0) {
    bgColor = '#10b981';
    textColor = '#fff';
    displayText = '0';
  } else if (waitTime <= 15) {
    bgColor = '#22c55e';
    textColor = '#fff';
    displayText = waitTime.toString();
  } else if (waitTime <= 30) {
    bgColor = '#84cc16';
    textColor = '#000';
    displayText = waitTime.toString();
  } else if (waitTime <= 45) {
    bgColor = '#eab308';
    textColor = '#000';
    displayText = waitTime.toString();
  } else if (waitTime <= 60) {
    bgColor = '#f97316';
    textColor = '#fff';
    displayText = waitTime.toString();
  } else {
    bgColor = '#ef4444';
    textColor = '#fff';
    displayText = waitTime.toString();
  }

  const borderColor = isWDS ? '#1e40af' : '#0d9488';
  const containerClass = isShow || isRestaurant ? 'marker-container show-marker' : 'marker-container';

  return L.divIcon({
    className: 'custom-wait-time-marker',
    html: `
      <div class="${containerClass}" style="background-color: ${bgColor}; border-color: ${borderColor};">
        <span class="marker-text" style="color: ${textColor}; display: flex; align-items: center; justify-content: center;">${displayText}</span>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20]
  });
};

// Fonction pour créer une icône de marqueur de transport
const createTransportIcon = (station, zoomLevel = 16) => {
  const { type, icon, color } = station;
  
  const showIcon = zoomLevel >= 16;
  const size = showIcon ? 44 : 28;
  const anchor = size / 2;
  
  return L.divIcon({
    className: 'custom-transport-marker',
    html: `
      <div class="transport-marker-container" style="background-color: white; border-color: ${color};" data-type="${type}">
        ${showIcon ? `<img src="${icon}" alt="${type}" class="transport-marker-icon" />` : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [anchor, anchor],
    popupAnchor: [0, -anchor]
  });
};

const createUserLocationIcon = () =>
  L.divIcon({
    className: 'user-location-marker',
    html: `
      <div class="user-location-marker-wrapper">
        <span class="user-location-pulse"></span>
        <span class="user-location-dot"></span>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });

const ParkInteractiveMap = ({ parkId, parkSlug, parkName, attractions, mapEntities, loading }) => {
  const { t, i18n } = useTranslation();
  const [showClosed, setShowClosed] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const {
    isLocating,
    locationError,
    permissionState,
    requestLocation,
    userLocation
  } = useMapUserLocation();
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const markersRef = useRef([]);
  const transportMarkersRef = useRef([]);
  const tileLayerRef = useRef(null);
  const userLocationLayersRef = useRef({ accuracyCircle: null, marker: null });
  const hasCenteredOnUserRef = useRef(false);
  
  const fallbackCoordinates = parkSlug ? getParkCoordinates(`/${parkSlug}`) : null;
  const parkConfig = useMemo(() => {
    if (PARK_CONFIG[parkId]) {
      return PARK_CONFIG[parkId];
    }

    return {
      center: fallbackCoordinates ? [fallbackCoordinates.lat, fallbackCoordinates.lng] : [48.8722, 2.7758],
      zoom: 16,
      locations: null,
      isWDS: false,
      usesDisneyTiles: false,
      showTransport: false
    };
  }, [parkId, fallbackCoordinates]);
  const locale = i18n.resolvedLanguage || i18n.language;
  const sourceEntities = Array.isArray(mapEntities)
    ? mapEntities
    : attractions;
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [tilesVersion, setTilesVersion] = useState(null);

  useEffect(() => {
    let isMounted = true;

    fetchMapTilesVersion(apiUrl).then((version) => {
      if (isMounted) {
        setTilesVersion(version || DEFAULT_MAP_TILES_VERSION);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [apiUrl]);

  // Fonction pour basculer le mode plein écran
  const toggleFullscreen = useCallback(() => {
    if (!mapWrapperRef.current) return;

    if (!document.fullscreenElement) {
      mapWrapperRef.current.requestFullscreen().catch(err => {
        console.error(`Erreur lors de l'activation du plein écran: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Écouter les changements de plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Invalidate map size after fullscreen change to fix rendering
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      }, 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Transformer les attractions pour la carte
  const mapAttractions = useMemo(() => {
    if (!Array.isArray(sourceEntities)) return [];

    return sourceEntities
      .map(item => normalizeMapEntity(item, { locations: parkConfig.locations, park: parkId.toLowerCase(), locale }))
      .filter(Boolean);
  }, [sourceEntities, parkConfig.locations, parkId, locale]);

  // Filtrer les attractions selon les options
  const filteredAttractions = useMemo(() => {
    return filterVisibleMapEntities(mapAttractions, showClosed);
  }, [mapAttractions, showClosed]);

  const isUserLocationNearMap = useMemo(() => {
    if (!userLocation) {
      return true;
    }

    return (
      calculateDistanceInMeters(
        userLocation.latitude,
        userLocation.longitude,
        parkConfig.center[0],
        parkConfig.center[1]
      ) <= (parkConfig.usesDisneyTiles ? 12000 : 15000)
    );
  }, [parkConfig.center, parkConfig.usesDisneyTiles, userLocation]);

  const getStatusText = useCallback((attraction) => {
    if (attraction.entityType === 'SHOW') {
      if (!attraction.showtimes || attraction.showtimes.length === 0) {
        return t('shows.noShowsToday', 'Pas de représentation');
      }
      return attraction.showtimes.join(', ');
    }

    if (attraction.entityType === 'RESTAURANT') {
      if (attraction.waitTime !== null && attraction.waitTime !== undefined && attraction.waitTime >= 0) {
        return `${attraction.waitTime} min`;
      }

      switch (attraction.status) {
        case 'CLOSED': return t('closed', 'Fermé');
        case 'DOWN': return t('outOfOrder', 'En panne');
        case 'REFURBISHMENT': return t('refurbishment', 'Réhabilitation');
        default: return t('restaurants.open', 'Ouvert');
      }
    }

    switch (attraction.status) {
      case 'CLOSED': return t('closed', 'Fermé');
      case 'DOWN': return t('outOfOrder', 'En panne');
      case 'REFURBISHMENT': return t('refurbishment', 'Réhabilitation');
      default:
        if (attraction.waitTime === -1) return t('noInfo', 'Pas d\'info');
        return `${attraction.waitTime} min`;
    }
  }, [t]);

  // Initialisation de la carte Leaflet
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: parkConfig.center,
      zoom: parkConfig.zoom,
      minZoom: parkConfig.usesDisneyTiles ? 15 : 10,
      maxZoom: parkConfig.usesDisneyTiles ? 18 : 19,
      zoomControl: true
    });

    mapRef.current = map;
    setMapInitialized(true);

    // Écouter les changements de zoom pour mettre à jour les marqueurs transport
    map.on('zoomend', () => {
      const currentZoom = map.getZoom();
      transportMarkersRef.current.forEach(({ marker, station }) => {
        const newIcon = createTransportIcon(station, currentZoom);
        marker.setIcon(newIcon);
      });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      if (tileLayerRef.current) {
        tileLayerRef.current.remove();
        tileLayerRef.current = null;
      }
    };
  }, [parkConfig]);

  useEffect(() => {
    hasCenteredOnUserRef.current = false;
  }, [parkId, parkSlug]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (tileLayerRef.current) {
      tileLayerRef.current.remove();
      tileLayerRef.current = null;
    }

    if (parkConfig.usesDisneyTiles) {
      if (!tilesVersion) return;

      tileLayerRef.current = L.tileLayer(
        buildMapTilesUrlTemplate(tilesVersion, apiUrl),
        MAP_TILES_LAYER_OPTIONS
      ).addTo(mapRef.current);
    } else {
      tileLayerRef.current = L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
          attribution: '&copy; OpenStreetMap contributors',
          minZoom: 10,
          maxZoom: 19
        }
      ).addTo(mapRef.current);
    }

    return () => {
      if (tileLayerRef.current) {
        tileLayerRef.current.remove();
        tileLayerRef.current = null;
      }
    };
  }, [tilesVersion, apiUrl, parkConfig]);

  // Mise à jour des marqueurs
  useEffect(() => {
    if (!mapRef.current) return;

    // Supprimer les anciens marqueurs
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Ajouter les nouveaux marqueurs
    filteredAttractions.forEach(attraction => {
      if (attraction.latitude == null || attraction.longitude == null) return;

      const icon = createWaitTimeIcon(
        attraction.waitTime,
        attraction.status,
        parkConfig.isWDS,
        attraction.entityType
      );

      const singleRiderHtml = attraction.singleRiderWaitTime !== null && attraction.singleRiderWaitTime >= 0
        ? `<div class="popup-single-rider">
            <span class="sr-label">${t('singleRider', 'Single Rider')}:</span>
            <span class="sr-value">${attraction.singleRiderWaitTime} min</span>
          </div>`
        : '';

      const popupContent = `
        <div class="popup-content">
          <h3 class="popup-title">${attraction.name}</h3>
          <div class="popup-wait-time">
            <span class="wait-label">${getMapEntityPrimaryLabel(attraction, t)}:</span>
            <span class="wait-value status-${(attraction.status || 'open').toLowerCase()}">
              ${getStatusText(attraction)}
            </span>
          </div>
          ${singleRiderHtml}
        </div>
      `;

      const marker = L.marker([attraction.latitude, attraction.longitude], { icon })
        .addTo(mapRef.current)
        .bindPopup(popupContent, { className: 'attraction-popup' });

      markersRef.current.push(marker);
    });

    // Nettoyer les anciens marqueurs transport
    transportMarkersRef.current.forEach(({ marker }) => marker.remove());
    transportMarkersRef.current = [];

    // Ajouter les marqueurs de transport (RER et SNCF)
    if (parkConfig.showTransport) {
      const currentZoom = mapRef.current.getZoom();
      Object.values(TRANSPORT_LOCATIONS).forEach(station => {
        const icon = createTransportIcon(station, currentZoom);
        
        const marker = L.marker([station.latitude, station.longitude], { icon })
          .addTo(mapRef.current);
        
        const popupContainer = document.createElement('div');
        const root = createRoot(popupContainer);
        
        const isSncf = station.type === 'SNCF' || station.id === 'SNCF_Chessy';
        const popupOptions = {
          className: 'transport-popup-wrapper',
          maxWidth: isSncf ? 560 : 340,
          minWidth: isSncf ? 500 : 300
        };
        
        marker.bindPopup(popupContainer, popupOptions);
        
        marker.on('popupopen', () => {
          root.render(<TransportPopup station={station} />);
        });
        
        marker.on('popupclose', () => {
          root.render(null);
        });
        
        markersRef.current.push(marker);
        transportMarkersRef.current.push({ marker, station });
      });
    }
  }, [filteredAttractions, t, getStatusText, parkConfig]);

  useEffect(() => {
    if (!mapRef.current) {
      return undefined;
    }

    const clearUserLocationLayers = () => {
      if (userLocationLayersRef.current.marker) {
        userLocationLayersRef.current.marker.remove();
        userLocationLayersRef.current.marker = null;
      }

      if (userLocationLayersRef.current.accuracyCircle) {
        userLocationLayersRef.current.accuracyCircle.remove();
        userLocationLayersRef.current.accuracyCircle = null;
      }
    };

    if (!userLocation) {
      clearUserLocationLayers();
      hasCenteredOnUserRef.current = false;
      return clearUserLocationLayers;
    }

    clearUserLocationLayers();

    const markerCoordinates = [userLocation.latitude, userLocation.longitude];

    if (Number.isFinite(userLocation.accuracy) && userLocation.accuracy > 0) {
      userLocationLayersRef.current.accuracyCircle = L.circle(markerCoordinates, {
        color: '#2563eb',
        fillColor: '#60a5fa',
        fillOpacity: 0.12,
        radius: Math.min(userLocation.accuracy, 150),
        weight: 1
      }).addTo(mapRef.current);
    }

    userLocationLayersRef.current.marker = L.marker(markerCoordinates, {
      icon: createUserLocationIcon()
    })
      .addTo(mapRef.current)
      .bindPopup(
        `<div class="popup-content"><h3 class="popup-title">${t('interactiveMap.locationPrompt.youAreHere', 'Vous êtes ici')}</h3></div>`,
        { className: 'attraction-popup' }
      );

    if (!hasCenteredOnUserRef.current && isUserLocationNearMap) {
      mapRef.current.flyTo(
        markerCoordinates,
        Math.max(mapRef.current.getZoom(), parkConfig.usesDisneyTiles ? 17 : 16),
        { duration: 1 }
      );
      hasCenteredOnUserRef.current = true;
    }

    return clearUserLocationLayers;
  }, [isUserLocationNearMap, parkConfig.usesDisneyTiles, t, userLocation]);

  return (
    <div className="park-interactive-map-container">
      <div className="map-controls park-map-controls">
        <label className="show-closed-toggle">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          <span>{t('interactiveMap.showClosed', 'Afficher les attractions fermées')}</span>
        </label>

        <div className="map-controls-right">
          {parkConfig.usesDisneyTiles && (
            <Link to="/map" className="filter-button map-all-parks-btn">
              <span className="map-all-parks-label">{t('interactiveMap.viewAllParks', 'Voir la carte des deux parcs')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </Link>
          )}

          <button
            className="fullscreen-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('interactiveMap.exitFullscreen', 'Quitter le plein écran') : t('interactiveMap.enterFullscreen', 'Plein écran')}
          >
            {isFullscreen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      <MapUserLocationNotice
        isLocating={isLocating}
        isLocationNearMap={isUserLocationNearMap}
        locationError={locationError}
        onRequestLocation={requestLocation}
        permissionState={permissionState}
        userLocation={userLocation}
      />

      <div
        ref={mapWrapperRef}
        className={`map-wrapper park-map-wrapper night-mode-active ${isFullscreen ? 'fullscreen' : ''}`}
      >
        {loading && !mapInitialized && (
          <div className="map-loading-overlay">
            <div className="loading-spinner"></div>
            <p>{t('loading', 'Chargement...')}</p>
          </div>
        )}
        <div ref={mapContainerRef} className="leaflet-map park-leaflet-map" />
        
        <div className="map-legend park-map-legend">
          <h4>{t('interactiveMap.legend', 'Légende')}</h4>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/images/buttons/webp/SHOWS_WHITE.webp" alt="Show" style={{ width: '12px', height: '12px' }} />
              </span>
              <span>{t('shows.title', 'Spectacles')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#0f766e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/images/buttons/webp/Restaurant_WHITE.webp" alt="Restaurant" style={{ width: '12px', height: '12px' }} />
              </span>
              <span>{t('restaurants.title', 'Restaurants')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#22c55e' }}></span>
              <span>0-15 min</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#84cc16' }}></span>
              <span>16-30 min</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#eab308' }}></span>
              <span>31-45 min</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#f97316' }}></span>
              <span>46-60 min</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#ef4444' }}></span>
              <span>60+ min</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#6b7280' }}>✕</span>
              <span>{t('closed', 'Fermé')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#ef4444' }}>⚠</span>
              <span>{t('outOfOrder', 'En panne')}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="map-disclaimer park-map-disclaimer">
        {parkConfig.usesDisneyTiles ? (
          <>
            <p>Certaines images/icônes/polices d&apos;écritures utilisées appartiennent à la Walt Disney Company.</p>
            <p>Magic Park Times n&apos;a absolument aucun intérêt commercial.</p>
            <p>Cette carte est une reproduction de la carte de l&apos;application mobile de Disneyland® Paris.</p>
            <p>Source des données: Themeparks.api &amp; DLPwait.</p>
          </>
        ) : (
          <>
            <p>{parkName || parkSlug || 'Parc'} · OpenStreetMap</p>
            <p>Source des données: Themeparks.api.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ParkInteractiveMap;
